// ---------------------------------------------------------------------------
// Knowledge Base — simulated wiki content (CLE-265).
//
// The KB page (/knowledge-base) is a markdown-first internal wiki. It exists so
// the landing page (CLE-262) can screenshot a populated "smart wiki". The
// content is hand-written, realistic Romanian documentation authored by the
// Clean Code Culture team. It is rendered directly by the page — no backend
// endpoint is required — so it is always available, including in Test Mode.
//
// Authors are pulled from the simulated team (testData/people.ts) so names stay
// coherent with the rest of the company dataset.
// ---------------------------------------------------------------------------

export interface KbDocument {
  /** URL-safe id, unique across the whole KB. */
  id: string;
  categoryId: string;
  title: string;
  /** Full markdown body (>= 200 words, with headings / lists / code blocks). */
  content: string;
  author: string;
  createdAt: string; // ISO date
  updatedAt: string; // ISO date
  tags: string[];
  /** Version label shown by the "version history" indicator. */
  version: string;
  /** Ids of related documents shown at the bottom of the viewer. */
  related: string[];
}

export interface KbCategory {
  id: string;
  label: string;
  /** lucide-react icon name resolved by the page. */
  icon: string;
  /** Short helper used for tooltips / empty states. */
  description: string;
  /** Optional external route (e.g. Meeting Notes aggregate). */
  externalTo?: string;
}

// --- Categories ------------------------------------------------------------

export const KB_CATEGORIES: KbCategory[] = [
  {
    id: 'procese-interne',
    label: 'Procese interne',
    icon: 'Workflow',
    description: 'Cum lucrăm: deploy, code review, onboarding client, SLA.',
  },
  {
    id: 'documentatie-tehnica',
    label: 'Documentație tehnică',
    icon: 'Code2',
    description: 'Arhitectură, convenții API și bază de date.',
  },
  {
    id: 'templates',
    label: 'Templates',
    icon: 'FileText',
    description: 'Șabloane reutilizabile pentru oferte, contracte, rapoarte.',
  },
  {
    id: 'meeting-notes',
    label: 'Meeting Notes',
    icon: 'NotebookPen',
    description: 'Notițe de ședință agregate pe proiecte.',
    externalTo: '/weekly-summary',
  },
  {
    id: 'onboarding',
    label: 'Onboarding',
    icon: 'GraduationCap',
    description: 'Integrarea noilor colegi și setup mediu.',
  },
  {
    id: 'proceduri-hr',
    label: 'Proceduri legale / HR',
    icon: 'Scale',
    description: 'Politici interne, concedii, evaluări.',
  },
];

// --- Documents -------------------------------------------------------------

export const KB_DOCUMENTS: KbDocument[] = [
  // === Procese interne ===
  {
    id: 'proces-deploy-productie',
    categoryId: 'procese-interne',
    title: 'Proces deploy producție',
    author: 'Sorin Lazar',
    createdAt: '2024-09-12',
    updatedAt: '2025-05-28',
    version: 'v4',
    tags: ['deploy', 'devops', 'ci-cd', 'producție'],
    related: ['code-review-checklist', 'sla-response-times', 'arhitectura-microservicii'],
    content: `# Proces deploy producție

Acest document descrie procesul standard de livrare în producție pentru toate proiectele Clean Code Culture. Scopul este zero-downtime, rollback rapid și trasabilitate completă.

## Precondiții

Înainte de orice deploy, asigură-te că:

- Toate testele automate trec pe \`main\` (unit + integration + e2e).
- Pull request-ul a fost aprobat de cel puțin un reviewer (vezi *Code review checklist*).
- Migrațiile de bază de date sunt **backward-compatible** (vezi convențiile DB).
- Există o intrare în changelog și ticket-ul Linear este în starea \`Ready for deploy\`.

## Pași

1. Creează un tag de release semantic: \`v<major>.<minor>.<patch>\`.
2. Rulează pipeline-ul de build și verifică artefactele.
3. Aplică migrațiile pe staging și rulează smoke tests.
4. Promovează imaginea către producție folosind strategia *blue-green*.

\`\`\`bash
# Build + push imagine
docker build -t registry.ccc.ro/app:v2.4.1 .
docker push registry.ccc.ro/app:v2.4.1

# Deploy blue-green prin Traefik
./deploy/promote.sh --image v2.4.1 --strategy blue-green
\`\`\`

## Rollback

Dacă smoke tests pe producție eșuează, rulează imediat:

\`\`\`bash
./deploy/promote.sh --rollback --to v2.4.0
\`\`\`

Rollback-ul este considerat reușit doar după ce health-check-ul revine verde și nu mai apar erori 5xx timp de 5 minute.

## Responsabilități

- **DevOps (Sorin)** deține pipeline-ul și aprobă fereastra de deploy.
- **PM** anunță clientul dacă deploy-ul are impact vizibil.
- **Dev autor** rămâne disponibil 30 de minute post-deploy pentru monitorizare.

> Regula de aur: nu facem deploy vineri după ora 15:00 fără aprobarea CTO.
`,
  },
  {
    id: 'code-review-checklist',
    categoryId: 'procese-interne',
    title: 'Code review checklist',
    author: 'Ioana Pop',
    createdAt: '2024-07-03',
    updatedAt: '2025-04-15',
    version: 'v6',
    tags: ['code-review', 'calitate', 'best-practices'],
    related: ['proces-deploy-productie', 'api-conventions', 'database-naming-conventions'],
    content: `# Code review checklist

Code review-ul este principalul nostru mecanism de calitate. Un review bun nu caută doar bug-uri — împărtășește context și ridică nivelul întregii echipe.

## Înainte de a cere review

- PR-ul este mic și focusat (ideal < 400 linii modificate).
- Titlul și descrierea explică *de ce*, nu doar *ce*.
- Ai rulat linter-ul și testele local.
- Ai adăugat teste pentru comportamentul nou.

## Ce verifică reviewer-ul

### Corectitudine
- Logica acoperă cazurile limită (null, listă goală, valori negative).
- Nu există race conditions sau efecte secundare neașteptate.

### Lizibilitate
- Numele sunt descriptive și consistente cu restul codebase-ului.
- Nu există cod mort sau comentat.

### Securitate
- Input-ul de la utilizator este validat la granița sistemului.
- Nu sunt expuse secrete sau credentiale în diff.

\`\`\`ts
// Bun: validare la granița sistemului
function parseLimit(raw: string): number {
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 1 || n > 100) {
    throw new BadRequest('limit must be between 1 and 100');
  }
  return n;
}
\`\`\`

## Etichetă

- Comentariile sunt despre cod, nu despre persoană.
- Folosește prefixe: \`nit:\` (minor), \`question:\`, \`blocking:\`.
- Aprobă doar ce ai înțeles efectiv.

> Țintă echipă: primul review în maxim 4 ore lucrătoare de la cerere.
`,
  },
  {
    id: 'proces-onboarding-client',
    categoryId: 'procese-interne',
    title: 'Proces onboarding client nou',
    author: 'Mihai Georgescu',
    createdAt: '2024-08-20',
    updatedAt: '2025-03-30',
    version: 'v3',
    tags: ['client', 'onboarding', 'delivery', 'kickoff'],
    related: ['sla-response-times', 'template-contract-prestari', 'template-oferta-comerciala'],
    content: `# Proces onboarding client nou

Primele două săptămâni cu un client nou stabilesc tonul întregii colaborări. Acest proces asigură că ajungem rapid la livrare predictibilă.

## Faza 1 — Kickoff (zilele 1-3)

- Ședință de kickoff cu toți stakeholderii.
- Confirmăm obiectivele de business și criteriile de succes.
- Stabilim canalele de comunicare (Slack shared channel + email).

## Faza 2 — Setup (zilele 4-7)

- Creăm proiectul în dashboard și pipeline-ul de leaduri devine proiect activ.
- Configurăm accesele: repo, staging, tool-uri de monitorizare.
- Definim cadența ședințelor (weekly status + demo bi-săptămânal).

## Faza 3 — Primul sprint (zilele 8-14)

1. Definim backlog-ul inițial împreună cu PO-ul clientului.
2. Estimăm și planificăm primul sprint.
3. Livrăm un *quick win* vizibil până la finalul primei săptămâni de sprint.

## Documente generate

- Contract de prestări servicii semnat (vezi template).
- Plan de proiect cu milestone-uri.
- Matrice RACI pentru responsabilități.

## Roluri pe parcursul onboarding-ului

- **PM-ul de cont** este punctul unic de contact și deține planul de proiect.
- **Tech lead-ul** validează accesele tehnice și arhitectura inițială.
- **CTO** aprobă orice decizie de arhitectură cu impact pe termen lung.

## Greșeli frecvente de evitat

- Pornirea dezvoltării înainte de a alinia criteriile de succes.
- Promisiuni de termene fără estimare comună cu echipa de delivery.
- Canale de comunicare neclare care duc la mesaje pierdute.

> Indicator de sănătate: dacă în 14 zile nu am livrat nimic demonstrabil, escaladăm la CTO.
`,
  },
  {
    id: 'sla-response-times',
    categoryId: 'procese-interne',
    title: 'SLA și response times',
    author: 'Elena Dumitrescu',
    createdAt: '2024-06-11',
    updatedAt: '2025-05-02',
    version: 'v2',
    tags: ['sla', 'suport', 'mentenanță', 'prioritate'],
    related: ['proces-deploy-productie', 'proces-onboarding-client'],
    content: `# SLA și response times

Definim nivelurile de serviciu pentru contractele de mentenanță. Aceste praguri sunt incluse în contractul de prestări servicii.

## Niveluri de prioritate

| Prioritate | Descriere | Timp de răspuns | Timp de rezolvare |
| --- | --- | --- | --- |
| P1 | Producție căzută | 1 oră | 4 ore |
| P2 | Funcționalitate critică afectată | 4 ore | 1 zi lucrătoare |
| P3 | Bug minor | 1 zi lucrătoare | 5 zile lucrătoare |
| P4 | Cerere de îmbunătățire | 2 zile lucrătoare | Planificat în backlog |

## Program de suport

- **Standard:** Luni–Vineri, 09:00–18:00 EET.
- **Extins (opțional):** 24/7 pentru incidente P1, cu retainer dedicat.

## Escaladare

1. Inginer de gardă confirmă incidentul.
2. Dacă P1 nu e stabilizat în 2 ore, se notifică CTO și PM-ul de cont.
3. Post-mortem obligatoriu în 48h pentru orice P1.

## Canale de raportare

- Incidentele se deschid prin formularul de suport sau pe canalul Slack dedicat clientului.
- Fiecare tichet primește automat o etichetă de prioritate, revizuită de inginerul de gardă.
- Statusul în timp real este vizibil în dashboard-ul de suport.

## Excluderi

SLA-ul nu acoperă: indisponibilitatea cauzată de servicii terțe ale clientului, cereri în afara scope-ului contractat, sau incidente provocate de modificări făcute direct de client în producție fără coordonare.

> Măsurăm aderența la SLA lunar și o raportăm clientului în raportul lunar.
`,
  },

  // === Documentație tehnică ===
  {
    id: 'arhitectura-microservicii',
    categoryId: 'documentatie-tehnica',
    title: 'Arhitectură microservicii',
    author: 'Ioana Pop',
    createdAt: '2024-05-18',
    updatedAt: '2025-06-01',
    version: 'v5',
    tags: ['arhitectură', 'microservicii', 'backend', 'infrastructură'],
    related: ['api-conventions', 'database-naming-conventions', 'proces-deploy-productie'],
    content: `# Arhitectură microservicii

Construim sisteme ca un set de servicii independente, deployabile separat, comunicând prin contracte explicite. Acest document descrie principiile noastre de referință.

## Principii

- **Un serviciu = o responsabilitate de business.** Granițele urmează domeniul, nu straturile tehnice.
- **Bază de date per serviciu.** Niciun serviciu nu citește direct tabelele altuia.
- **Comunicare asincronă by default.** Evenimentele sunt preferate apelurilor sincrone acolo unde e posibil.

## Stack de referință

\`\`\`text
Frontend (React + Vite)
        │  REST / JSON
        ▼
API Gateway (Traefik)
        │
   ┌────┴─────┬──────────┬───────────┐
   ▼          ▼          ▼           ▼
 auth-svc  billing-svc  crm-svc   notify-svc
   │          │          │           │
   ▼          ▼          ▼           ▼
 Postgres  Postgres   Postgres    Redis
\`\`\`

## Comunicare între servicii

- Sincron: REST cu timeout strict (max 3s) și circuit breaker.
- Asincron: cozi de mesaje pentru evenimente de domeniu (\`invoice.paid\`, \`lead.converted\`).

## Observabilitate

Fiecare serviciu expune:

1. \`/health\` — liveness + readiness.
2. Metrice Prometheus pe \`/metrics\`.
3. Logging structurat JSON cu \`traceId\` propagat din gateway.

## Reziliență

- **Retry cu backoff** exponențial pentru apelurile idempotente.
- **Idempotency keys** pe operațiunile de scriere care pot fi reluate.
- **Bulkheads**: izolăm pool-urile de conexiuni per dependență, ca o problemă să nu cadă tot serviciul.

## Granițe și anti-pattern-uri

Evităm "monolitul distribuit": dacă două servicii trebuie deployate mereu împreună, probabil sunt de fapt unul singur. Reevaluăm granițele când vedem cuplaj puternic sau tranzacții care traversează mai multe servicii.

> Regulă: niciun serviciu nou nu intră în producție fără health-check, metrice și loguri structurate.
`,
  },
  {
    id: 'api-conventions',
    categoryId: 'documentatie-tehnica',
    title: 'API conventions',
    author: 'Alexandru Ionescu',
    createdAt: '2024-06-22',
    updatedAt: '2025-05-19',
    version: 'v4',
    tags: ['api', 'rest', 'convenții', 'backend'],
    related: ['database-naming-conventions', 'arhitectura-microservicii', 'code-review-checklist'],
    content: `# API conventions

Convenții pentru toate API-urile REST pe care le construim. Consistența reduce surprizele pentru consumatori (frontend, integrări, parteneri).

## URL-uri și resurse

- Substantive la plural: \`/api/invoices\`, \`/api/clients\`.
- Resurse imbricate maxim un nivel: \`/api/projects/:id/tasks\`.
- Kebab-case pentru segmente multi-cuvânt: \`/api/bank-statements\`.

## Metode HTTP

| Metodă | Utilizare |
| --- | --- |
| GET | Citire, idempotent |
| POST | Creare |
| PATCH | Update parțial |
| DELETE | Ștergere |

## Format răspuns

Toate răspunsurile urmează o anvelopă consistentă:

\`\`\`json
{
  "success": true,
  "data": { "id": "inv_123", "total": 4200 },
  "meta": { "page": 1, "pageSize": 20 }
}
\`\`\`

## Erori

\`\`\`json
{
  "success": false,
  "error": { "code": "VALIDATION_ERROR", "message": "limit must be 1..100" }
}
\`\`\`

- Folosim coduri HTTP corecte (400, 401, 403, 404, 409, 422, 500).
- Codurile de eroare interne sunt \`SCREAMING_SNAKE_CASE\` și stabile.

## Paginare și filtrare

- \`?page=\` + \`?limit=\` (max 100).
- Sortare cu \`?sort=field\` și \`?order=asc|desc\`.

## Autentificare și autorizare

- Token \`Bearer\` în header-ul \`Authorization\`.
- Permisiunile sunt verificate pe server, niciodată doar în UI.
- Răspundem \`401\` pentru lipsă de autentificare și \`403\` pentru lipsă de drepturi.

## Idempotență

Operațiunile \`POST\` care pot fi reluate acceptă un header \`Idempotency-Key\`. Serverul reține rezultatul primei execuții și îl returnează la reapeluri cu aceeași cheie, prevenind dublarea (de ex. emiterea aceleiași facturi de două ori).

> Versionăm prin breaking-change discipline, nu prin \`/v2\` în URL — preferăm compatibilitate înapoi.
`,
  },
  {
    id: 'database-naming-conventions',
    categoryId: 'documentatie-tehnica',
    title: 'Database naming conventions',
    author: 'Bogdan Marin',
    createdAt: '2024-07-09',
    updatedAt: '2025-04-28',
    version: 'v3',
    tags: ['database', 'postgres', 'convenții', 'schema'],
    related: ['api-conventions', 'arhitectura-microservicii'],
    content: `# Database naming conventions

Convenții pentru schema PostgreSQL. Numele predictibile fac query-urile și migrațiile mai sigure.

## Tabele

- Plural, \`snake_case\`: \`invoices\`, \`bank_statements\`.
- Tabele de legătură: \`<a>_<b>\` alfabetic: \`project_user\`.

## Coloane

- \`snake_case\`, fără prefix de tabel.
- Cheie primară: \`id\` (UUID sau bigint identity).
- Chei străine: \`<resursă>_id\` → \`client_id\`, \`project_id\`.
- Timestamps: \`created_at\`, \`updated_at\` (cu timezone).
- Boolean: prefix \`is_\` / \`has_\`: \`is_active\`, \`has_vat\`.

## Indecși și constrângeri

\`\`\`sql
-- Index pe cheie străină + nume explicit
CREATE INDEX idx_invoices_client_id ON invoices (client_id);

-- Constrângere unică explicită
ALTER TABLE users
  ADD CONSTRAINT uq_users_email UNIQUE (email);
\`\`\`

## Migrații

- O migrație = o schimbare logică.
- Migrațiile sunt **backward-compatible**: adaugă coloane nullable, populează, apoi impune NOT NULL într-un pas ulterior.
- Niciodată \`DROP COLUMN\` în același release în care codul încă o folosește.

## Tipuri de date

- Sume monetare: \`numeric(12, 2)\`, niciodată \`float\` — evităm erorile de rotunjire.
- Identificatori externi: \`text\`, nu \`varchar(n)\` arbitrar.
- Enum-uri: coloană \`text\` cu \`CHECK\` constraint, mai ușor de migrat decât tipurile \`enum\` native.
- Timpi: stocăm mereu în UTC; conversia în fusul orar local se face la prezentare.

## Soft delete

Preferăm \`deleted_at timestamptz NULL\` în loc de ștergere fizică pentru entitățile de business (facturi, clienți). Query-urile aplicației filtrează implicit \`deleted_at IS NULL\` printr-un view dedicat, astfel încât istoricul rămâne auditabil.

> Aplicăm aceste reguli automat prin linterul de schemă în CI.
`,
  },

  // === Templates ===
  {
    id: 'template-oferta-comerciala',
    categoryId: 'templates',
    title: 'Template ofertă comercială',
    author: 'Andrei Pata',
    createdAt: '2024-09-01',
    updatedAt: '2025-05-10',
    version: 'v2',
    tags: ['ofertă', 'vânzări', 'template'],
    related: ['template-contract-prestari', 'proces-onboarding-client'],
    content: `# Template ofertă comercială

Șablon standard pentru ofertele trimise leadurilor calificate. Personalizează secțiunile marcate cu \`[...]\`.

## Antet

- **Către:** [Nume companie client]
- **De la:** Clean Code Culture SRL
- **Dată:** [DD.MM.YYYY]
- **Valabilitate ofertă:** 30 de zile

## Context și obiective

[Rezumă în 2-3 fraze problema clientului și rezultatul dorit, folosind limbajul lui.]

## Soluția propusă

- Faza 1: [Descriere + livrabile]
- Faza 2: [Descriere + livrabile]
- Faza 3: [Descriere + livrabile]

## Investiție

| Fază | Livrabil | Estimare (zile-om) | Preț |
| --- | --- | --- | --- |
| 1 | [...] | [...] | [...] EUR |
| 2 | [...] | [...] | [...] EUR |

## Termeni

- Facturare lunară pe baza progresului.
- Plată la 14 zile de la emiterea facturii.
- Proprietatea intelectuală se transferă la achitarea integrală.

## Ce ne diferențiază

[Include 2-3 puncte de diferențiere relevante: expertiză pe domeniul clientului, livrare predictibilă, calitate prin testare automată, comunicare transparentă.]

## Pașii următori

1. Confirmarea ofertei prin email.
2. Semnarea contractului de prestări servicii.
3. Ședința de kickoff în maxim 5 zile lucrătoare.

> Înainte de trimitere: verifică marja minimă și aprobarea CEO pentru oferte > 25.000 EUR.
`,
  },
  {
    id: 'template-contract-prestari',
    categoryId: 'templates',
    title: 'Template contract prestări servicii',
    author: 'Monica Sandu',
    createdAt: '2024-08-15',
    updatedAt: '2025-03-22',
    version: 'v3',
    tags: ['contract', 'legal', 'template'],
    related: ['template-oferta-comerciala', 'sla-response-times', 'politica-concedii'],
    content: `# Template contract prestări servicii

Structura standard a contractului de prestări servicii software. A se folosi împreună cu departamentul juridic pentru clauze specifice.

## Părți

- **Prestator:** Clean Code Culture SRL, CUI [...], înregistrată la [...].
- **Beneficiar:** [Denumire client], CUI [...].

## Obiectul contractului

Prestatorul se obligă să furnizeze servicii de dezvoltare software conform anexei tehnice (SOW).

## Clauze esențiale

1. **Durată:** [perioadă] cu reînnoire automată dacă nu se notifică cu 30 de zile înainte.
2. **Preț și plată:** conform ofertei acceptate; penalități de întârziere 0.1%/zi.
3. **Niveluri de serviciu:** conform documentului *SLA și response times*.
4. **Confidențialitate:** valabilă 3 ani după încetare.
5. **Proprietate intelectuală:** transfer la plata integrală.
6. **Limitarea răspunderii:** plafonată la valoarea ultimelor 3 facturi.

## Încetare

- De comun acord, în scris.
- Pentru neexecutare, cu preaviz de 15 zile.

\`\`\`text
Semnături:
Prestator: ____________________   Data: __________
Beneficiar: ___________________   Data: __________
\`\`\`

## Anexe obligatorii

- **Anexa 1 — SOW:** descrierea tehnică a livrabilelor.
- **Anexa 2 — Acord de prelucrare a datelor (DPA):** când se procesează date personale, conform GDPR.
- **Anexa 3 — Plan de plăți:** scadențar și condiții de facturare.

## Forță majoră

Niciuna dintre părți nu răspunde pentru neexecutare cauzată de evenimente independente de voința sa (calamități, întreruperi majore de infrastructură). Partea afectată notifică în 5 zile și depune diligențe pentru reluarea activității.

> Orice abatere de la acest șablon trebuie aprobată de departamentul juridic.
`,
  },
  {
    id: 'template-raport-lunar',
    categoryId: 'templates',
    title: 'Template raport lunar client',
    author: 'Elena Dumitrescu',
    createdAt: '2024-10-02',
    updatedAt: '2025-05-31',
    version: 'v2',
    tags: ['raport', 'client', 'template', 'delivery'],
    related: ['sla-response-times', 'proces-onboarding-client'],
    content: `# Template raport lunar client

Raportul lunar menține transparența și încrederea clientului. Se trimite în primele 5 zile lucrătoare ale lunii.

## Sumar executiv

[2-3 fraze: ce s-a livrat luna trecută și starea generală a proiectului.]

## Livrabile finalizate

- [Funcționalitate 1] — livrată în [data].
- [Funcționalitate 2] — livrată în [data].

## Metrice cheie

| Metrică | Luna curentă | Luna anterioară |
| --- | --- | --- |
| Story points livrate | [...] | [...] |
| Bug-uri deschise | [...] | [...] |
| Aderență SLA | [...]% | [...]% |
| Uptime | [...]% | [...]% |

## Planuri pentru luna următoare

1. [Obiectiv 1]
2. [Obiectiv 2]

## Riscuri și blocaje

- [Risc] — [plan de mitigare].

## Financiar

- Ore consumate vs. bugetate: [...] / [...].
- Sumă facturată luna aceasta: [...] EUR.

## Feedback și satisfacție

- Scor de satisfacție al clientului (1-5) colectat trimestrial.
- Subiecte ridicate de client și statusul lor.
- Acțiuni de îmbunătățire asumate de echipa noastră.

## Anexe

- Capturi din demo-ul lunar.
- Link către board-ul de sprint pentru transparență completă.

> PM-ul de cont validează raportul înainte de trimitere; metricele se preiau din dashboard.
`,
  },

  // === Onboarding ===
  {
    id: 'onboarding-developer-nou',
    categoryId: 'onboarding',
    title: 'Onboarding developer nou',
    author: 'Ioana Pop',
    createdAt: '2024-07-01',
    updatedAt: '2025-06-05',
    version: 'v7',
    tags: ['onboarding', 'developer', 'echipă'],
    related: ['setup-mediu-dezvoltare', 'code-review-checklist', 'api-conventions'],
    content: `# Onboarding developer nou

Bun venit în echipă! Acest ghid te aduce de la zero la primul PR făcut în maxim 3 zile.

## Ziua 1 — Acces și context

- Primești conturi: email, Slack, repo, dashboard, tool-uri de monitorizare.
- Citește documentele esențiale: *Arhitectură microservicii*, *API conventions*, *Code review checklist*.
- Cunoaște-ți mentorul (un dev senior) și PM-ul echipei.

## Ziua 2 — Mediu și primul build

- Urmează ghidul *Setup mediu dezvoltare*.
- Rulează aplicația local și testele.
- Alege un ticket marcat \`good-first-issue\`.

## Ziua 3 — Primul PR

1. Lucrează ticket-ul pe un branch dedicat.
2. Scrie teste pentru schimbarea ta.
3. Deschide PR și cere review mentorului.

## Cultura noastră

- **Clean code, mereu.** Lăsăm codul mai bun decât l-am găsit.
- **Întrebările sunt binevenite.** Nu pierde mai mult de 30 de minute blocat fără să întrebi.
- **Ownership.** Cine scrie codul îl și monitorizează după deploy.

## Resurse

- Wiki intern (acest Knowledge Base).
- Canalul Slack \`#dev-help\` pentru întrebări rapide.

## Obiective pentru prima lună

- Săptămâna 1: mediu funcțional + primul PR mergat.
- Săptămâna 2: prima funcționalitate end-to-end livrată cu teste.
- Săptămâna 3-4: contribuții independente la un proiect activ.

## Cu cine vorbești

- **Mentor:** întrebări tehnice și review.
- **PM:** prioritizare și context de business.
- **CTO:** arhitectură, carieră, feedback.

> Check-in cu CTO la finalul primei săptămâni pentru feedback bidirecțional.
`,
  },
  {
    id: 'setup-mediu-dezvoltare',
    categoryId: 'onboarding',
    title: 'Setup mediu dezvoltare',
    author: 'Sorin Lazar',
    createdAt: '2024-07-02',
    updatedAt: '2025-05-20',
    version: 'v5',
    tags: ['setup', 'dev', 'docker', 'onboarding'],
    related: ['onboarding-developer-nou', 'arhitectura-microservicii'],
    content: `# Setup mediu dezvoltare

Pași pentru a avea aplicația rulând local. Estimare: sub o oră pe o mașină curată.

## Cerințe

- Node.js 20 LTS și npm 10.
- Docker Desktop (sau Docker Engine + Compose).
- Git configurat cu cheia ta SSH.

## Pași

\`\`\`bash
# 1. Clonează repo-ul
git clone git@github.com:ccc/app.git
cd app

# 2. Instalează dependențele
npm install

# 3. Pregătește variabilele de mediu
cp .env.example .env

# 4. Pornește infrastructura (db, redis) în Docker
docker compose up -d

# 5. Rulează migrațiile
npm run db:migrate

# 6. Pornește backend + frontend
npm run dev
\`\`\`

## Verificare

- Frontend disponibil pe \`http://localhost:5173\`.
- Backend health-check verde pe \`http://localhost:3000/health\`.
- Rulează \`npm test\` — toate testele trebuie să treacă.

## Probleme frecvente

- **Port ocupat:** oprește procesul cu \`lsof -i :5173\` sau schimbă portul în \`.env\`.
- **Migrații eșuate:** verifică că Docker rulează și containerul \`postgres\` e \`healthy\`.
- **Dependențe stricate:** șterge \`node_modules\` și \`package-lock.json\`, apoi \`npm install\` din nou.

## Tooling recomandat

- Editor: VS Code cu extensiile ESLint, Prettier și GitLens.
- Git hooks rulează automat lint + format la commit (husky).
- Folosește \`.editorconfig\`-ul din repo pentru indentare consistentă.

## Comenzi utile

\`\`\`bash
npm run lint     # verifică stilul codului
npm run build    # build de producție
npm run db:reset # resetează baza de date locală
\`\`\`

> Dacă te blochezi peste 30 de minute, scrie în \`#dev-help\` — cineva te ajută.
`,
  },

  // === Proceduri HR ===
  {
    id: 'politica-concedii',
    categoryId: 'proceduri-hr',
    title: 'Politica concedii',
    author: 'Monica Sandu',
    createdAt: '2024-06-05',
    updatedAt: '2025-02-18',
    version: 'v3',
    tags: ['hr', 'concedii', 'politică'],
    related: ['evaluare-performanta-trimestriala', 'template-contract-prestari'],
    content: `# Politica concedii

Politica internă privind concediile, conform Codului Muncii și practicilor Clean Code Culture.

## Concediu de odihnă

- **25 de zile lucrătoare** pe an pentru toți angajații full-time.
- Se acumulează proporțional pentru angajații part-time și cei intrați în cursul anului.
- Maxim 5 zile se pot reporta în anul următor (folosite până la 31 martie).

## Cum soliciți concediu

1. Anunță managerul direct cu cel puțin 2 săptămâni înainte pentru concedii > 3 zile.
2. Creează cererea în sistemul HR și asigură-te că e aprobată.
3. Predă responsabilitățile critice și actualizează statusul ticketelor.

## Alte tipuri de concediu

- **Concediu medical:** pe baza certificatului, conform legii.
- **Evenimente speciale:** căsătorie (5 zile), deces rudă apropiată (3 zile).
- **Concediu fără plată:** la solicitare, cu aprobarea managerului și a HR.

## Acoperire în absență

- Pentru orice absență > 2 zile, desemnează un coleg de backup.
- Nu lăsăm clientul fără punct de contact: PM-ul preia comunicarea.

## Work from anywhere

- Munca remote este implicită; nu monitorizăm prezența, ci rezultatele.
- Pentru deplasări lungi în alt fus orar, anunță echipa din timp pentru a alinia orele de overlap.

## Sărbători legale

Zilele libere legale din România se adaugă peste cele 25 de zile de concediu de odihnă. Dacă o sărbătoare cade în weekend, nu se reportează automat, conform legislației în vigoare.

> Perioadele cu cerere mare (decembrie, august) se planifică din timp pentru a păstra acoperirea echipei.
`,
  },
  {
    id: 'evaluare-performanta-trimestriala',
    categoryId: 'proceduri-hr',
    title: 'Evaluare performanță trimestrială',
    author: 'Andrei Pata',
    createdAt: '2024-06-08',
    updatedAt: '2025-04-10',
    version: 'v4',
    tags: ['hr', 'evaluare', 'performanță', 'feedback'],
    related: ['politica-concedii', 'onboarding-developer-nou'],
    content: `# Evaluare performanță trimestrială

Evaluarea trimestrială este o conversație structurată despre creștere, nu un examen. Scopul este aliniere și dezvoltare.

## Cadență

- O dată la trei luni, programată de manager.
- Durată: 45-60 de minute, 1-la-1.

## Cele patru dimensiuni

1. **Impact** — rezultate livrate și valoare adusă echipei/clientului.
2. **Calitate tehnică** — code quality, decizii de arhitectură, fiabilitate.
3. **Colaborare** — comunicare, mentorat, code review.
4. **Creștere** — învățare, asumarea de responsabilități noi.

## Structura ședinței

- Auto-evaluare prealabilă (completată de angajat cu 2 zile înainte).
- Feedback bidirecțional pe fiecare dimensiune.
- Stabilirea a 2-3 obiective concrete pentru trimestrul următor.

## Scala de evaluare

| Nivel | Semnificație |
| --- | --- |
| Depășește | Constant peste așteptările rolului |
| Atinge | Livrează solid la nivelul rolului |
| În dezvoltare | Progres vizibil, are nevoie de sprijin |

## După evaluare

- Obiectivele se notează în sistemul HR și se revizuiesc la jumătatea trimestrului.
- Discuțiile de salarizare se leagă de ciclul anual, nu de fiecare evaluare.

## Pregătirea managerului

- Strânge exemple concrete din ultimul trimestru, nu impresii generale.
- Solicită feedback de la 2-3 colegi cu care persoana a colaborat direct.
- Pregătește atât aprecieri specifice, cât și zone clare de creștere.

## Plan de dezvoltare individual

Fiecare evaluare se încheie cu un plan scurt: o competență de dezvoltat, o resursă (curs, mentor, proiect) și un indicator de succes verificabil la următoarea evaluare. Planul aparține angajatului; managerul îl sprijină.

> Feedback-ul cald și specific bate feedback-ul amânat și general. Documentăm exemple concrete.
`,
  },
];

// --- Helpers ---------------------------------------------------------------

/** Number of documents in a given category. */
export function kbDocCountByCategory(categoryId: string): number {
  return KB_DOCUMENTS.filter((d) => d.categoryId === categoryId).length;
}

/** Resolve a document by its id. */
export function kbDocById(id: string): KbDocument | undefined {
  return KB_DOCUMENTS.find((d) => d.id === id);
}

/** Resolve a category by its id. */
export function kbCategoryById(id: string): KbCategory | undefined {
  return KB_CATEGORIES.find((c) => c.id === id);
}
